import { Fragment, ReactNode } from 'react';
import clsx from 'clsx';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import { CRORE, isEmpty, validateCIN } from '../../../../../../utils/utils';
import { DATE_FORMATS, formatDateByFormat } from '../../../../../../utils/dateUtils';
import { BASIC_DETAILS_LABELS } from './constants';
import styles from './BasicDetailsSidebar.module.scss';
import detailStyles from '../Details/Details.module.scss';

/** MCA registry fields from `customFields`; amounts are whole rupees and `0` is a real value. */
export interface BasicDetailsCustomFields {
    efilingStatus?: string;
    lastFilingDate?: string;
    lastAgmDate?: string;
    leiNumber?: string;
    leiStatus?: string;
    leiRegistrationDate?: string;
    leiLastUpdatedDate?: string;
    leiNextRenewalDate?: string;
    panStatus?: string;
    dataAsOf?: number;
    paidUpCapital?: number;
    authorizedCapital?: number;
    sumOfCharges?: number;
    activeCompliance?: string;
}

interface BasicDetailsSidebarProps {
    panNumber?: string;
    cinNumber?: string;
    gstin?: any[];
    headquarterLocation?: { fullAddress?: string };
    /**
     * Already formatted for display by the caller. Lives here rather than in the
     * Company Details grid because the main overview API returns foundationDate even
     * when the overview is LOCKED, and this card still renders in that state.
     */
    foundedDate?: string | null;
    customFields?: BasicDetailsCustomFields;
    /** The address otherwise lives in the All Known Details card; set wherever that card is absent. */
    showAddress?: boolean;
}

/** ₹ Cr from a crore up, plain rupees below. */
const formatAmount = (amount?: number): string => {
    if (amount == null || Number.isNaN(amount)) return '';
    return amount >= CRORE
        ? `₹${(amount / CRORE).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Cr`
        : `₹${amount.toLocaleString('en-IN')}`;
};

const toTitleCase = (text: string): string => text.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

/** Active and Issued are live (green); every other state is red. */
const isPositiveStatus = (status: string): boolean => /^(active|issued)\b/i.test(status.trim()) && !/\bnon\b/i.test(status);

const InfoGlyph = () => (
    <svg className={styles.InfoGlyph} width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="12" cy="8" r="1.1" fill="currentColor" />
    </svg>
);

const DetailRow = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className={styles.Row}>
        <span className={styles.Label}>{label}</span>
        {children}
    </div>
);

const DetailTile = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className={styles.Tile}>
        <span className={styles.TileLabel}>{label}</span>
        {children}
    </div>
);

const BasicDetailsSidebar = ({
    panNumber,
    cinNumber,
    gstin,
    headquarterLocation,
    foundedDate,
    customFields,
    showAddress = false,
}: BasicDetailsSidebarProps) => {
    const {
        efilingStatus,
        lastFilingDate,
        lastAgmDate,
        leiNumber,
        leiStatus,
        activeCompliance,
        panStatus,
        dataAsOf,
        leiRegistrationDate,
        leiLastUpdatedDate,
        leiNextRenewalDate,
    } = customFields ?? {};
    const dataAsOfLabel = dataAsOf ? formatDateByFormat(dataAsOf, DATE_FORMATS.MONTH_SHORT_YEAR) : '';
    // Authority and LOU have no source field, so only sourced rows show.
    const leiFacts = [
        { label: BASIC_DETAILS_LABELS.LEI_REGISTRATION_DATE, value: leiRegistrationDate },
        { label: BASIC_DETAILS_LABELS.LEI_LAST_UPDATE, value: leiLastUpdatedDate },
        { label: BASIC_DETAILS_LABELS.LEI_NEXT_RENEWAL, value: leiNextRenewalDate },
    ]
        .filter((fact) => !isEmpty(fact.value))
        .map((fact) => ({ ...fact, value: formatDateByFormat(fact.value as string, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY) }));

    const lastFilingDateValue = formatDateByFormat(lastFilingDate, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY);
    const lastAgmDateValue = formatDateByFormat(lastAgmDate, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY);

    const amountTiles = [
        { label: BASIC_DETAILS_LABELS.PAID_UP_CAPITAL, value: formatAmount(customFields?.paidUpCapital) },
        { label: BASIC_DETAILS_LABELS.AUTHORIZED_CAPITAL, value: formatAmount(customFields?.authorizedCapital) },
        { label: BASIC_DETAILS_LABELS.SUM_OF_CHARGES, value: formatAmount(customFields?.sumOfCharges) },
    ].filter((tile) => tile.value);

    const hasTiles = amountTiles.length > 0 || !isEmpty(activeCompliance);

    const showEntityStatus = !isEmpty(efilingStatus);
    const showLastFilingDate = !isEmpty(lastFilingDateValue);
    const showLastAgm = !isEmpty(lastAgmDateValue);
    const showLei = !isEmpty(leiNumber);
    const fullAddress = headquarterLocation?.fullAddress;
    const showAddressRow = showAddress && !isEmpty(fullAddress);

    const hasData =
        foundedDate ||
        (panNumber && panNumber !== '') ||
        (cinNumber && cinNumber !== '') ||
        (gstin && gstin.length > 0) ||
        hasTiles ||
        showEntityStatus ||
        showLastFilingDate ||
        showLastAgm ||
        showLei ||
        showAddressRow;

    if (!hasData) return null;

    const getGstInValue = (gstinObj: any) => {
        const [, gstinValue] = Object.entries(gstinObj)[0] as [string, string];
        return gstinValue;
    };

    const getToolTipContent = (gstinList: any[]) => (
        <div className={styles.GstTooltipList}>
            {gstinList?.map((gstinObj) => {
                const [stateName, gstinValue] = Object.entries(gstinObj)[0] as [string, string];
                return (
                    <div className={detailStyles.FormerEntityTooltip} key={`${stateName}-${gstinValue}`}>
                        <div className={detailStyles.FormerEntityName}>
                            <span className={styles.GstStateName}>{stateName}:</span> <span>{gstinValue}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const leiTooltip = (
        <div className={styles.LeiTooltip}>
            <span className={styles.LeiTooltipLabel}>{BASIC_DETAILS_LABELS.LEI_STATUS}</span>
            <span
                className={clsx(
                    styles.LeiTooltipValue,
                    isPositiveStatus(leiStatus ?? '') ? styles.LeiTooltipStatusPositive : styles.LeiTooltipStatusNegative,
                )}
            >
                {toTitleCase(leiStatus ?? '')}
            </span>
            {leiFacts.map((fact) => (
                <Fragment key={fact.label}>
                    <span className={styles.LeiTooltipLabel}>{fact.label}</span>
                    <span className={styles.LeiTooltipValue}>{fact.value}</span>
                </Fragment>
            ))}
        </div>
    );

    return (
        <div className={styles.BasicDetailsSidebar}>
            <div className={styles.Header}>
                <div className={styles.Heading}>{BASIC_DETAILS_LABELS.HEADING}</div>
                {dataAsOfLabel && <span className={styles.DataAsOf}>{`${BASIC_DETAILS_LABELS.DATA_AS_OF} ${dataAsOfLabel}`}</span>}
            </div>
            <div className={styles.Body}>
                <div className={styles.Rows}>
                    {foundedDate && (
                        <DetailRow label={BASIC_DETAILS_LABELS.FOUNDED}>
                            <span className={styles.Value}>{foundedDate}</span>
                        </DetailRow>
                    )}
                    {showEntityStatus && (
                        <DetailRow label={BASIC_DETAILS_LABELS.ENTITY_STATUS}>
                            <span
                                className={clsx(
                                    styles.StatusPill,
                                    isPositiveStatus(efilingStatus as string) ? styles.StatusPillPositive : styles.StatusPillNegative,
                                )}
                            >
                                {toTitleCase(efilingStatus as string)}
                            </span>
                        </DetailRow>
                    )}
                    {showLastFilingDate && (
                        <DetailRow label={BASIC_DETAILS_LABELS.LAST_FILING_DATE}>
                            <span className={styles.Value}>{lastFilingDateValue}</span>
                        </DetailRow>
                    )}
                    {showLastAgm && (
                        <DetailRow label={BASIC_DETAILS_LABELS.LAST_AGM}>
                            <span className={styles.Value}>{lastAgmDateValue}</span>
                        </DetailRow>
                    )}
                    {panNumber && (
                        <DetailRow label={BASIC_DETAILS_LABELS.PAN}>
                            <div className={styles.ValueWithStatus}>
                                <span className={styles.Value}>{panNumber}</span>
                                {!isEmpty(panStatus) && (
                                    <span
                                        className={clsx(
                                            styles.StatusText,
                                            isPositiveStatus(panStatus as string) ? styles.StatusPositive : styles.StatusNegative,
                                        )}
                                    >
                                        {toTitleCase(panStatus as string)}
                                    </span>
                                )}
                            </div>
                        </DetailRow>
                    )}
                    {cinNumber && (
                        <DetailRow label={validateCIN(cinNumber) ? BASIC_DETAILS_LABELS.CIN : BASIC_DETAILS_LABELS.LLPIN}>
                            <span className={styles.Value}>{cinNumber}</span>
                        </DetailRow>
                    )}
                    {gstin && gstin.length > 0 && (
                        <DetailRow label={BASIC_DETAILS_LABELS.GST}>
                            <span className={styles.Value}>
                                {getGstInValue(gstin[0])}
                                {gstin.length > 1 && (
                                    <>
                                        {', '}
                                        <Tooltip
                                            content={getToolTipContent(gstin.slice(1))}
                                            direction="left"
                                            isOverviewDetail={true}
                                            usePortal={true}
                                            fitContent={true}
                                            hideDelay={500}
                                            style={{ width: 'max-content', minWidth: '18rem', maxWidth: '26.25rem' }}
                                            contentCss={{ maxHeight: '14rem', overflowY: 'auto' }}
                                        >
                                            <span className={styles.MoreLink}>+{gstin.length - 1} more</span>
                                        </Tooltip>
                                    </>
                                )}
                            </span>
                        </DetailRow>
                    )}
                    {showLei && (
                        <DetailRow label={BASIC_DETAILS_LABELS.LEI}>
                            <div className={styles.ValueWithStatus}>
                                <span className={styles.Value}>{leiNumber}</span>
                                {!isEmpty(leiStatus) && (
                                    <span
                                        className={clsx(
                                            styles.StatusText,
                                            styles.LeiStatus,
                                            isPositiveStatus(leiStatus as string) ? styles.StatusPositive : styles.StatusNegative,
                                        )}
                                    >
                                        {toTitleCase(leiStatus as string)}
                                        {leiFacts.length > 0 && (
                                            <Tooltip
                                                content={leiTooltip}
                                                direction="left"
                                                isOverviewDetail={true}
                                                usePortal={true}
                                                fitContent={true}
                                                hideDelay={500}
                                                style={{ width: 'max-content' }}
                                            >
                                                <InfoGlyph />
                                            </Tooltip>
                                        )}
                                    </span>
                                )}
                            </div>
                        </DetailRow>
                    )}
                    {showAddressRow && (
                        <DetailRow label={BASIC_DETAILS_LABELS.ADDRESS}>
                            <span className={styles.Value}>{fullAddress}</span>
                        </DetailRow>
                    )}
                </div>
                {hasTiles && (
                    <div className={styles.TileGrid}>
                        {amountTiles.map(({ label, value }) => (
                            <DetailTile key={label} label={label}>
                                <span className={styles.TileValue}>{value}</span>
                            </DetailTile>
                        ))}
                        {!isEmpty(activeCompliance) && (
                            <DetailTile label={BASIC_DETAILS_LABELS.MCA_COMPLIANCE}>
                                <span
                                    className={clsx(
                                        styles.TileValueText,
                                        isPositiveStatus(activeCompliance as string) ? styles.StatusPositive : styles.StatusNegative,
                                    )}
                                >
                                    {toTitleCase(activeCompliance as string)}
                                </span>
                            </DetailTile>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BasicDetailsSidebar;
